import {
  GetAllPlatformCatalogVersionsParams,
  getGetAllPlatformCatalogVersionsQueryKey,
  getGetPlatformCatalogByIdQueryKey,
  PaginatedPlatformCatalogVersionListItemsDTO,
  PlatformCatalogVersionListItemDTO,
  useDeletePlatformCatalogVersionById,
  useGetAllPlatformCatalogVersions,
} from "@bvhi/backend-api";
import { Card, CardContent, CardHeader, GridPaginationModel, GridSortModel } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../../../components/datagrid/bvhi-datagrid";
import { useAuthContext } from "../../../../../../../context/use-auth-context";
import { SecuredDeleteDialog } from "../../../../../../../dialogs/secured-delete-dialog";
import { useToast } from "../../../../../../../hooks/use-toast";
import { Route } from "../../../../../../../routes/_main/app/catalogs.catalog.$catalogId.index";
import { toNarrowedTypeOrNull } from "../../../../../../../utils/ts";
import { useCatalogVersionsDataGridDefinitions } from "../../hooks/use-catalog-versions-data-grid-definition";
import { useCatalogVersionsSortParams } from "../../hooks/use-catalog-versions-sort-params";

interface Props {
  requestParams: GetAllPlatformCatalogVersionsParams;
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
}

export const CatalogVersionsDataGrid = forwardRef<HTMLDivElement, Props>(({ updateCallback, requestParams }, ref) => {
  const { catalogId } = useParams({ strict: false });
  if (!catalogId) throw new Error("catalogId not found in path");

  const { t } = useTranslation(["catalogs", "dialogs"]);
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { mutate: deletePlatformCatalogVersionById } = useDeletePlatformCatalogVersionById();
  const { sort, page, size } = Route.useSearch();
  const { getSortModelForBackendString } = useCatalogVersionsSortParams();

  const [catalogVersionIdToDelete, setCatalogVersionIdToDelete] = useState<string | null>(null);
  const { pushDeleteSuccessToast, pushDeleteErrorToast } = useToast();

  const { data: getAllPlatformCatalogVersionsResponse, isFetching: getAllPlatformCatalogVersionsIsFetching } =
    useGetAllPlatformCatalogVersions(currentUser?.organisation?.id ?? "", catalogId, requestParams, {
      query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
    });

  const handleDelete = (versionId: string) => {
    setCatalogVersionIdToDelete(versionId);
  };

  const { columns } = useCatalogVersionsDataGridDefinitions({ handleDelete, catalogId });

  const handleCornfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      if (!confirmed || !catalogVersionIdToDelete || !currentUser?.organisation?.id) {
        setCatalogVersionIdToDelete(null);
        return;
      }
      void deletePlatformCatalogVersionById(
        {
          supplierId: currentUser.organisation.id,
          platformCatalogId: catalogId,
          platformCatalogVersionId: catalogVersionIdToDelete,
        },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("catalogs:catalogs.catalog"));
          },
          onError: () => {
            pushDeleteErrorToast(t("catalogs:catalogs.catalog"));
          },
          onSettled: () => {
            setCatalogVersionIdToDelete(null);
            void queryClient.invalidateQueries({
              queryKey: getGetAllPlatformCatalogVersionsQueryKey(currentUser.organisation?.id ?? "", catalogId),
            });
            void queryClient.invalidateQueries({
              queryKey: getGetPlatformCatalogByIdQueryKey(currentUser.organisation?.id ?? "", catalogId),
            });
          },
        }
      );
    },
    [
      catalogVersionIdToDelete,
      currentUser,
      deletePlatformCatalogVersionById,
      catalogId,
      queryClient,
      pushDeleteSuccessToast,
      t,
      pushDeleteErrorToast,
    ]
  );

  const catalogVersions = toNarrowedTypeOrNull<PaginatedPlatformCatalogVersionListItemsDTO>(
    getAllPlatformCatalogVersionsResponse?.data,
    "numberOfItems"
  );

  return (
    <div ref={ref}>
      <Card sx={{ minHeight: 300 }}>
        <CardHeader
          subheader={t("catalogs:catalogs.cards.catlog.versions.card.sub.title")}
          title={t("catalogs:catalogs.cards.catlog.versions.card.title")}
        />
        <CardContent restrictContentWidth={false}>
          <BVHIDataGrid<PlatformCatalogVersionListItemDTO>
            columns={columns}
            initalPaginationModel={{ page: page ? page : 0, pageSize: size ? size : 25 }}
            initialSortModel={getSortModelForBackendString(sort && sort.length >= 1 ? sort[0] : "")}
            loading={getAllPlatformCatalogVersionsIsFetching}
            rowCount={catalogVersions?.numberOfItems ?? -1}
            rows={catalogVersions?.payload ?? []}
            totalPages={catalogVersions?.numberOfPages ?? 0}
            updateCallback={updateCallback}
          />
        </CardContent>
      </Card>
      <SecuredDeleteDialog
        onClose={handleCornfirmDialogClose}
        open={catalogVersionIdToDelete !== null}
        overwrite={{ title: t("dialogs:dialogs.delete.catalog.version.title") }}
      />
    </div>
  );
});
CatalogVersionsDataGrid.displayName = "CatalogVersionsDataGrid";
